import { useEffect, useState, type FormEvent } from 'react';
import { fetchClientFraudRules, saveClientFraudRules } from '../api/endpoints';
import type { ClientDecisionRule } from '../api/types';
import { toErrorMessage } from '../api/client';
import { useApiData } from '../hooks/useApiData';
import { ErrorState, LoadingState } from './LoadingState';
import { StatusBadge } from './StatusBadge';
import { useToast } from './ToastProvider';

/** The two rule groups (DecisionRule.Group), each its own card. Shared with Platform Settings. */
export const RULE_GROUPS = [
  {
    key: 'Document',
    title: 'Hard rules',
    icon: 'bi-shield-lock',
    tint: 'text-primary',
    help: 'The document and the session themselves: expired or unreadable dates, a missing step, an underage holder.',
  },
  {
    key: 'Fraud',
    title: 'Fraud checks',
    icon: 'bi-shield-exclamation',
    tint: 'text-danger',
    help: 'Signs of a forged or reused document. Each finding is always an alert; by default Critical and High decline, Medium and Low only alert.',
  },
];

interface FraudRulesFormProps {
  /** Omitted: the signed-in client's own rules (Settings → Fraud Rules). Set: Client detail (SuperAdmin). */
  clientId?: number;
  /** Fields disabled and no buttons (client users other than ClientAdmin). */
  readOnly?: boolean;
}

/**
 * One client's decision rules over the platform's (Platform Settings → Decision rules). A switch
 * the client sets wins over the platform's; a rule it leaves alone follows the platform, including
 * later platform changes. Setting a switch back to the platform's value makes it follow the
 * platform again. The rules, the storage and the audit rows are Core's IDecisionRuleService.
 */
export function FraudRulesForm({ clientId, readOnly = false }: FraudRulesFormProps) {
  const { showSuccess, showError } = useToast();
  const { data, loading, error } = useApiData(() => fetchClientFraudRules(clientId), [clientId]);
  const [rules, setRules] = useState<ClientDecisionRule[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) setRules(data);
  }, [data]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!rules || readOnly) return;
    setSaving(true);
    try {
      setRules(await saveClientFraudRules(Object.fromEntries(rules.map((r) => [r.code, r.declines])), clientId));
      showSuccess('Fraud rules saved.');
    } catch (err) {
      showError(toErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function toggle(code: string) {
    setRules((current) =>
      current
        ? current.map((r) =>
            r.code === code ? { ...r, declines: !r.declines, overridden: !r.declines !== r.platformDeclines } : r,
          )
        : current,
    );
  }

  /** Every switch back to the platform's; takes effect on Save. */
  function resetToPlatform() {
    setRules((current) =>
      current ? current.map((r) => ({ ...r, declines: r.platformDeclines, overridden: false })) : current,
    );
  }

  if (loading) return <LoadingState label="Loading fraud rules" />;
  if (error) return <ErrorState message={error} />;
  if (!rules) return null;

  const customCount = rules.filter((r) => r.overridden).length;

  return (
    <form onSubmit={handleSubmit}>
      <p className="text-body-secondary small">
        Every verification ends Approved or Declined. A rule that is on declines a verification that
        raises it; off, it is recorded and, for fraud checks, raised as an alert only. Rules start as
        the platform sets them; a rule switched here applies to this account only. A critical field
        below the confidence threshold, a failed face match and a failed liveness check always
        decline. Each change is audited.
      </p>

      {RULE_GROUPS.map((g) => {
        const groupRules = rules.filter((r) => r.group === g.key);
        const declining = groupRules.filter((r) => r.declines).length;
        return (
          <div key={g.key} className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-0 fw-semibold">
                  <i className={`bi ${g.icon} me-2 ${g.tint}`} />
                  {g.title}
                </h6>
                <div className="small text-body-secondary mt-1">{g.help}</div>
              </div>
              <span className="badge badge-outline theme-secondary rounded-pill text-nowrap ms-3">
                {declining} of {groupRules.length} decline
              </span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-dashboard table-hover align-middle mb-0 decision-rules-table">
                  <thead>
                    <tr>
                      <th className="col-declines">Declines</th>
                      <th className="col-rule">Rule</th>
                      <th className="col-severity">Severity</th>
                      <th className="col-platform">Platform</th>
                      <th className="d-none d-md-table-cell">What it checks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupRules.map((r) => (
                      <tr key={r.code}>
                        <td>
                          <div className="form-check form-switch d-inline-block mb-0">
                            <input
                              id={`fraud-rule-${r.code}`}
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={r.declines}
                              disabled={readOnly}
                              onChange={() => toggle(r.code)}
                              aria-label={`${r.label} declines the verification`}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="fw-medium">
                            {r.label}
                            {r.overridden && (
                              <span
                                className="ms-2 badge badge-status bg-primary-subtle text-primary border border-primary-subtle"
                                title="Set for this account; differs from the platform"
                              >
                                Custom
                              </span>
                            )}
                          </div>
                          <code className="small">{r.code}</code>
                        </td>
                        <td>
                          {/* Same severity badge as Fraud Alerts: it is the same FraudSeverity. */}
                          <StatusBadge status={r.severity} />
                        </td>
                        <td className="small text-body-secondary text-nowrap">
                          {r.platformDeclines ? 'Declines' : 'Alert only'}
                        </td>
                        <td className="d-none d-md-table-cell small text-body-secondary">{r.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })}

      {!readOnly && (
        <div className="d-flex flex-wrap align-items-center gap-2">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            <i className="bi bi-floppy me-1" />
            {saving ? 'Saving…' : 'Save fraud rules'}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={saving || customCount === 0}
            onClick={resetToPlatform}
          >
            <i className="bi bi-arrow-counterclockwise me-1" />
            Reset to platform
          </button>
          <span className="small text-body-secondary ms-1">
            {customCount === 0 ? 'Every rule follows the platform.' : `${customCount} set for this account.`}
          </span>
        </div>
      )}
    </form>
  );
}
